import { createSlice } from "@reduxjs/toolkit";
import axios from "../axios";
import axiosInstanceWithoutInterceptor from "../axiosWithoutInterceptor";
import getConfig from "../config";
import {
    customerRelationshipOptions,
    getCustomerRelationShip,
} from "../features/search/Filters/utility";
import toast from "../hooks/toast";
import { buildApiV3Url, buildApiV3UrlWithQuery, formatAddress } from "../shared/Utils";

// Config is retrieved lazily inside functions to support runtime configuration
const getHostnames = () => getConfig().hostnames;

const initialState = {
    isLoading: false,
    page: {
        data: [],
        paginate: {
            totalRecords: 0,
            startIndex: 0,
            endIndex: 0,
            rowsPerPage: 0,
        },
    },
    formState: {
        customerCode: "",
        customerName: "",
        customerState: [],
        exposureZone: [],
        customerRelationship: [],
    },
    sortColumn: ["customerNumber", true],
    isExportMissingCertificatesLoading: false,
    isExportMissingCertificatesDialogOpen: false,
    isScheduleDialogOpen: false,
    isScheduleLoading: false,
};

const [showToast, hideToast] = toast();

// Helper function to build missing certificates criteria
const buildMissingCertificatesCriteria = (formState, sortColumn, options = {}) => {
    const {
        includeClientId = false,
        includePagination = false,
        useExportFormat = false,
        title = null,
        activeCompany = null,
        pageSize = null,
        pageNumber = null,
    } = options;

    // Convert customer relationship filter values to API format
    let isBillTo = null;
    let isShipTo = null;

    if (formState.customerRelationship?.length > 0) {
        isBillTo = getCustomerRelationShip(
            formState.customerRelationship,
            customerRelationshipOptions.value.isBillTo
        );
        isShipTo = getCustomerRelationShip(
            formState.customerRelationship,
            customerRelationshipOptions.value.isShipTo
        );
    }

    const [orderBy, orderByDirection] = sortColumn;

    // Map sort column to API format for export
    const mapSortColumnToApi = column => {
        switch (column) {
            case "customerNumber":
                return "customer_number";
            case "customerName":
                return "customer_name";
            case "alternateId":
                return "alternate_id";
            case "customerCreationDate":
                return "created";
            default:
                return "customer_name";
        }
    };

    const criteria = {};

    // Add title if provided (for export)
    if (title) {
        criteria.title = title;
    }

    // Add customer code/number
    if (useExportFormat) {
        criteria.customerNumber = formState.customerCode?.trim() || "";
    } else {
        criteria.customerCode = formState.customerCode?.trim() || null;
    }

    // Add customer name
    if (useExportFormat) {
        criteria.customerName = formState.customerName?.trim() || "";
    } else {
        criteria.customerName = formState.customerName?.trim() || null;
    }

    // Add exposure zones
    if (useExportFormat) {
        criteria.exposureZones = formState.exposureZone?.length ? formState.exposureZone : [];
    } else {
        criteria.exposureZones = formState.exposureZone?.length ? formState.exposureZone : null;
    }

    // Add customer states/regions
    if (useExportFormat) {
        criteria.customerStates = formState.customerState?.length ? formState.customerState : [];
    } else {
        criteria.customerRegions = formState.customerState?.length ? formState.customerState : null;
    }

    // Add customer relationship filters
    criteria.isBillTo = isBillTo;
    criteria.isShipTo = isShipTo;

    // Add sorting
    if (useExportFormat) {
        criteria.orderBy = mapSortColumnToApi(orderBy);
        criteria.orderByDirection = orderByDirection ? "ASC" : "DESC";
    } else {
        criteria.orderBy = orderBy || "customerCode";
        criteria.orderByDirection = orderByDirection ? "asc" : "desc";
    }

    // Add pagination if requested
    if (includePagination && pageSize && pageNumber) {
        criteria.pageSize = pageSize;
        criteria.pageNumber = pageNumber;
    }

    // Add client ID if requested (for export)
    if (includeClientId && activeCompany) {
        criteria.clientId = [activeCompany.id];
    }

    return criteria;
};

// =============================================================================
// SELECTORS - Centralize all state query logic
// =============================================================================

export const selectIsLoading = state => {
    return state.missingCertificates.isLoading;
};

export const selectPage = state => {
    return state.missingCertificates.page;
};

export const selectFormState = state => {
    return state.missingCertificates.formState;
};

export const selectSortColumn = state => {
    return state.missingCertificates.sortColumn;
};

export const selectIsExportMissingCertificatesLoading = state => {
    return state.missingCertificates.isExportMissingCertificatesLoading;
};

export const selectIsDialogOpenMissingCertificatesExport = state => {
    return state.missingCertificates.isExportMissingCertificatesDialogOpen;
};

export const selectIsScheduleDialogOpen = state => {
    return state.missingCertificates.isScheduleDialogOpen;
};

export const selectIsScheduleLoading = state => {
    return state.missingCertificates.isScheduleLoading;
};

// =============================================================================
// REDUCERS - Group reducer logic together for state transitions
// =============================================================================

const missingCertificatesSlice = createSlice({
    name: "missingCertificates",
    initialState,
    reducers: {
        setIsLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setPageToNull: state => {
            state.page = {
                data: [],
                paginate: {
                    totalRecords: 0,
                    startIndex: 0,
                    endIndex: 0,
                    rowsPerPage: 0,
                },
            };
        },
        setFormState: (state, action) => {
            state.formState = action.payload;
        },
        setSortColumn: (state, action) => {
            state.sortColumn = action.payload;
        },
        setIsLoadingMissingCertificatesExport: (state, action) => {
            state.isExportMissingCertificatesLoading = action.payload;
        },
        setIsDialogOpenMissingCertificatesExport: (state, action) => {
            state.isExportMissingCertificatesDialogOpen = action.payload;
        },
        setIsScheduleDialogOpen: (state, action) => {
            state.isScheduleDialogOpen = action.payload;
        },
        setIsScheduleLoading: (state, action) => {
            state.isScheduleLoading = action.payload;
        },
    },
});

export const {
    setIsLoading,
    setPage,
    setPageToNull,
    setFormState,
    setSortColumn,
    setIsLoadingMissingCertificatesExport,
    setIsDialogOpenMissingCertificatesExport,
    setIsScheduleDialogOpen,
    setIsScheduleLoading,
} = missingCertificatesSlice.actions;

export default missingCertificatesSlice.reducer;

// =============================================================================
// THUNKS (ASYNC ACTIONS) - Isolate side-effect handling and async flows
// =============================================================================

export const fetchMissingCertificatesReport =
    (pageSize, pageNumber) => async (dispatch, getState) => {
        const { isLoading, formState, sortColumn } = getState().missingCertificates;
        const { rowsPerPage } = getState().session;

        if (isLoading) {
            return;
        }

        dispatch(setIsLoading(true));

        try {
            const finalPageSize = pageSize || rowsPerPage || 20;
            const finalPageNumber = pageNumber || 1;

            // Build request payload using the criteria builder
            const requestPayload = buildMissingCertificatesCriteria(formState, sortColumn, {
                includePagination: true,
                useExportFormat: false,
                pageSize: finalPageSize,
                pageNumber: finalPageNumber,
            });

            // Make API call to missing-certificates-customers endpoint
            const apiResponse = await axios.post(
                buildApiV3Url(`missing-certificates-customers`),
                requestPayload,
                { withCredentials: true }
            );

            if (!apiResponse || !apiResponse.data) {
                dispatch(setPageToNull());
                return;
            }

            // Transform API response to match grid expectations
            const transformedData = apiResponse.data.value.map(item => ({
                id: item.id,
                customerNumber: item.customerCode,
                name: item.customerName,
                address: formatAddress(
                    [item.addressLine1, item.addressLine2, item.city, item.state],
                    { initials: item.country },
                    null
                ),
                exposureZone: item.exposureZone,
                billTo: item.isBillTo,
                shipTo: item.isShipTo,
                alternateId: item.alternateId,
                customerCreationDate: item.customerCreationDate,
            }));

            // Calculate pagination info
            const startIndex = (finalPageNumber - 1) * finalPageSize;
            const pageData = {
                data: transformedData,
                paginate: {
                    totalRecords: apiResponse.data.count,
                    startIndex,
                    endIndex: startIndex + transformedData.length,
                    rowsPerPage: finalPageSize,
                },
            };

            dispatch(setPage(pageData));
            // eslint-disable-next-line no-unused-vars
        } catch (error) {
            dispatch(setPageToNull());
            showToast("error", "Failed to fetch missing certificates report");
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const postMissingCertificatesExportAPI = title => async (dispatch, getState) => {
    const { isExportMissingCertificatesLoading, formState, sortColumn } =
        getState().missingCertificates;
    const { activeCompany } = getState().session;

    if (isExportMissingCertificatesLoading) {
        return;
    }

    dispatch(setIsLoadingMissingCertificatesExport(true));

    try {
        // Build request payload using the criteria builder
        const requestPayload = buildMissingCertificatesCriteria(formState, sortColumn, {
            includeClientId: false,
            useExportFormat: true,
            title,
            activeCompany,
        });

        const response = await axios
            .post(buildApiV3Url(`export-customer-missing-certificate-report`), requestPayload, {
                withCredentials: true,
            })
            .catch(() => {
                showToast(
                    "error",
                    "Failed to export Missing Certificates Report. Please try again later."
                );
            });

        dispatch(setIsDialogOpenMissingCertificatesExport(false));

        if (response?.status === 200) {
            const baseURL = `https://${getHostnames().docLockerClient}`;
            window.open(`${baseURL}/mylockers/downloads`, "_blank", "noopener,noreferrer");
            showToast(
                "success",
                "We are working on your export. When it's ready, find it in",
                "export-success",
                true,
                "your downloads",
                () => {
                    hideToast("export-success");
                    window.open(`${baseURL}/mylockers/downloads`, "_blank", "noopener,noreferrer");
                },
                "The time it takes to export data depends on the size of the file and network traffic."
            );
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error exporting missing certificates report:", error);
        showToast("error", "Failed to export Missing Certificates Report. Please try again later.");
    } finally {
        dispatch(setIsLoadingMissingCertificatesExport(false));
    }
};

export const postSavedSearchAndScheduleEmailAPI = days => async (dispatch, getState) => {
    const { isScheduleLoading, formState, sortColumn } = getState().missingCertificates;
    const { activeCompany, userId } = getState().session;

    if (isScheduleLoading) {
        return;
    }

    dispatch(setIsScheduleLoading(true));

    try {
        // Step 1: Create saved search using POST api/v3/savedSearches
        const criteria = buildMissingCertificatesCriteria(formState, sortColumn, {
            useExportFormat: true,
            includeClientId: true,
            activeCompany,
        });

        const savedSearchPayload = {
            aiUserId: userId,
            cannedSearch: "User",
            searchType: "MissingCertificatesCustomers",
            title: "Customer Missing Certificates report",
            criteria,
            avaTaxCompanyId: activeCompany.id,
        };

        const apiURI = buildApiV3UrlWithQuery("savedsearches", "", "", 0);
        const savedSearchResponse = await axiosInstanceWithoutInterceptor.post(
            apiURI,
            savedSearchPayload,
            { withCredentials: true }
        );

        if (!savedSearchResponse?.data?.id) {
            throw new Error("Failed to create saved search");
        }

        // Step 2: Schedule the saved search using PUT api/v3/companies/{companyId}/scheduled-email-reports
        const schedulePayload = {
            SavedSearchesEcmId: savedSearchResponse.data.id,
            Days: days,
        };

        const scheduleResponse = await axios.put(
            buildApiV3Url("scheduled-email-reports"),
            schedulePayload,
            { withCredentials: true }
        );

        if (scheduleResponse?.status === 200) {
            dispatch(setIsScheduleDialogOpen(false));
            showToast("success", "Missing Certificates Report scheduled successfully.");
        } else {
            throw new Error("Failed to schedule report");
        }
        // eslint-disable-next-line no-unused-vars
    } catch (error) {
        showToast(
            "error",
            "Failed to schedule Missing Certificates Report. Please try again later."
        );
    } finally {
        dispatch(setIsScheduleLoading(false));
    }
};
